import {isNil} from 'ramda';

import {
  AuctionVehicleDetailResponseBody,
  AuctionVehicleResponseBody,
  SourcingSuspiciousReason,
  SourcingVehicleDetailResponseBody,
} from '@dms/api/core';

import {SOURCING_SUSPICIOUS_REASONS} from '../constants/sourcingSuspiciousReasons';
import {isHiddenAuctionType} from './isHiddenAuctionType';
import {resolveAuctionEnumLabel} from './resolveAuctionEnumLabel';

/**
 * Auctions still use SourcingTable internals, so we adapt auction API rows to
 * the SourcingVehicleDetailResponseBody-like shape as much as possible.
 */

/**
 * Auction detail adapted to a sourcing-detail-compatible shape.
 *
 * `SourcingVehicleDetailResponseBody` is the primary compatibility contract.
 * We only append `auctionVehicle` as the auction-specific payload needed by
 * auction table/detail consumers.
 *
 * Tech debt: remove once sourcing views are fully generic for auction payloads.
 * [T20-115646]
 */
export type AuctionSourcingListItem = SourcingVehicleDetailResponseBody &
  AuctionVehicleDetailResponseBody;

/**
 * Adapts `AuctionVehicleDetailResponseBody` into a
 * `SourcingVehicleDetailResponseBody`-compatible object.
 *
 * The result keeps `auctionVehicle` nested (original auction payload) and maps
 * only the sourcing fields needed for compatibility with sourcing detail flows.
 *
 * Tech debt: remove once sourcing views are fully generic for auction payloads.
 * [T20-115646]
 */
// eslint-disable-next-line complexity -- Pre-existing violation
export function adaptAuctionVehicleToSourcingItem(
  auction: AuctionVehicleDetailResponseBody,
  enumLookup: ReadonlyMap<string, string>
): AuctionSourcingListItem {
  const auctionVehicle = auction.auctionVehicle;
  const isHidden = isHiddenAuctionType(auctionVehicle.auctionType);
  const adId = auctionVehicle.adId ?? '';
  const vehicleId = auctionVehicle.pk ?? adId;
  // `carStatus` is the authoritative lifecycle of this vehicle in the auction.
  // An ended auction vehicle is not necessarily sold; `carSold` specifically means Buy Now.
  const isSold = Boolean(auctionVehicle.carSold);
  const isAvailable = auctionVehicle.carStatus !== 'ended';

  return {
    ...auction,
    carvagoBuy: false,
    sourcingVehicle: {
      id: vehicleId,
      adId,
      isAvailable,
      isSold,
      justSold: null, // Live classifieds availability checks do not apply to auction vehicles.
      carStyle: toTranslation(auctionVehicle.carStyle, enumLookup),
      // Auctions publish no emissions, but they do publish both dates.
      carbonDioxideEmission: null,
      carbonDioxideEmissionOrigin: null,
      registrationDate: auctionVehicle.registrationDate ?? null,
      manufactureDate: auctionVehicle.manufactureDate ?? null,
      color: {
        color: toTranslation(auctionVehicle.color, enumLookup),
        specification: toTranslation(auctionVehicle.colorSpecification, enumLookup),
      },
      cubicCapacity: auctionVehicle.cubicCapacity ?? null,
      daysOnStock: null,
      doorCount: toTranslation(auctionVehicle.doorCount, enumLookup),
      driveType: toTranslation(auctionVehicle.driveType, enumLookup),
      featureScore: auctionVehicle.featureScore ?? null,
      features: toTranslations(auctionVehicle.features, enumLookup),
      fuelType: toTranslation(auctionVehicle.fuelType, enumLookup),
      highlightFeatures: toTranslations(auctionVehicle.highlightFeatures, enumLookup),
      interiorMaterial: toTranslation(auctionVehicle.interiorMaterial, enumLookup),
      make: toTranslation(auctionVehicle.make, enumLookup),
      mileage: auctionVehicle.mileage ?? null,
      model: toTranslation(auctionVehicle.model, enumLookup),
      modelFamilyGroup: toTranslation(auctionVehicle.modelFamilyGroup, enumLookup),
      numberOfSeats: auctionVehicle.numberOfSeats ?? null,
      occurrence: null,
      originCountry: toTranslation(auctionVehicle.originCountry, enumLookup),
      power: auctionVehicle.power ?? null,
      price: {
        withVat: isHidden
          ? null
          : (auctionVehicle.priceActual ?? auctionVehicle.priceBuyNow ?? null),
        vatRate: auctionVehicle.vatRate ?? null,
        vatReclaimable: auctionVehicle.vatReclaimable ?? null,
        currency: toTranslation(auctionVehicle.currency, enumLookup),
        originalCurrency: toTranslation(auctionVehicle.originalCurrency, enumLookup),
        originalWithVat: isHidden
          ? null
          : (auctionVehicle.originalPriceActual ?? auctionVehicle.originalPriceBuyNow ?? null),
      },
      seller: {
        name: auctionVehicle.seller?.name ?? null,
        type: toTranslation(auctionVehicle.seller?.type ?? null, enumLookup),
        address: auctionVehicle.seller?.address ?? null,
        city: auctionVehicle.seller?.city ?? null,
        zip: auctionVehicle.seller?.zip ?? null,
        email: auctionVehicle.seller?.email ?? null,
        phone: auctionVehicle.seller?.phone ?? null,
        rating: {
          average: auctionVehicle.seller?.ratingAverage ?? null,
          count: auctionVehicle.seller?.ratingCount ?? null,
        },
        country: toTranslation(auctionVehicle.sellerCountry, enumLookup),
      },
      server: auctionVehicle.server ?? null,
      similarity: null,
      technicalInspectionValidTo: auctionVehicle.technicalInspectionValidTo ?? null,
      thumbnailUrl: {full: auctionVehicle.thumbnailUrl ?? null},
      transmission: toTranslation(auctionVehicle.transmission, enumLookup),
      turnover: auctionVehicle.turnover ?? null,
      url: {full: auctionVehicle.url ?? null},
      proxyUrl: {full: auctionVehicle.url ?? null},
      vin: auctionVehicle.vin ?? null,
      year: auctionVehicle.year ?? null,
      maximalPossibleMargin: isHidden ? null : (auctionVehicle.maximalPossibleMargin ?? null),
      extraData: {
        marketPosition: auctionVehicle.marketPosition ?? null,
        marketPositionCount: auctionVehicle.marketPositionCount ?? null,
        maximalPossibleMargin: isHidden ? null : (auctionVehicle.maximalPossibleMargin ?? null),
        predictedPrice: auctionVehicle.predictedPrice ?? null,
      },
      trimLevel: auctionVehicle.trimLevel ?? null,
      adTitle: auctionVehicle.adTitle ?? null,
      description: auctionVehicle.description ?? null,
      pictureUrls: auctionVehicle.picturesUrl ?? [],
      suspicious: Boolean(auctionVehicle.suspicious),
      suspiciousReason: toSourcingSuspiciousReasons(auctionVehicle.suspiciousReason),
      attractivenessScore: auctionVehicle.attractivenessScore ?? null,
      marketSituationCategory: toSourcingMarketSituationCategory(
        auctionVehicle.marketSituationCategory
      ),
      priceDynamicsCategory: toSourcingPriceDynamicsCategory(auctionVehicle.priceDynamicsCategory),
      isBlacklist: Boolean(auctionVehicle.isBlacklist),
      isHitlist: Boolean(auctionVehicle.isHitlist),
      priceChanges: [],
      priceChangesDates: [],
      pricePercentile: null,
    },
    summary: {
      position: auctionVehicle.marketPosition ?? null,
      positionFrom: auctionVehicle.marketPositionCount ?? null,
      margin: isHidden ? null : (auctionVehicle.maximalPossibleMargin ?? null),
      original: isHidden ? null : (auctionVehicle.originalPriceActual ?? null),
      purchase: isHidden ? null : (auctionVehicle.priceActual ?? null),
      retail: auctionVehicle.predictedPrice ?? null,
      lid: null,
      dod: null,
      rank: auction.auctionVehicle.featureScore ?? null,
      vehicleCosts: null,
      adPrice: isHidden ? null : (auctionVehicle.priceActual ?? null),
    },
    vehicleSummary: auction.vehicleSummary ?? null,
    features: auction.features,
    highlights: auction.highlights ?? [],
    shortcomings: auction.shortcomings ?? [],
  };
}

function toTranslation(value: string | null | undefined, enumLookup: ReadonlyMap<string, string>) {
  if (isNil(value)) {
    return null;
  }

  return {key: value, translation: resolveAuctionEnumLabel(value, enumLookup) ?? value};
}

function toTranslations(
  values: string[] | null | undefined,
  enumLookup: ReadonlyMap<string, string>
) {
  if (isNil(values)) {
    return [];
  }

  return values.map((value) => ({
    key: value,
    translation: resolveAuctionEnumLabel(value, enumLookup) ?? value,
  }));
}

function toSourcingSuspiciousReasons(
  reasons: AuctionVehicleResponseBody['suspiciousReason']
): SourcingSuspiciousReason[] {
  const knownReasons: SourcingSuspiciousReason[] = [...SOURCING_SUSPICIOUS_REASONS];

  if (isNil(reasons)) {
    return [];
  }

  return reasons.filter((reason): reason is SourcingSuspiciousReason =>
    knownReasons.includes(reason)
  );
}

function toSourcingMarketSituationCategory(
  value: AuctionVehicleResponseBody['marketSituationCategory']
) {
  if (
    value === 'MARKET_SITUATION_MAJOR_SHORTAGE' ||
    value === 'MARKET_SITUATION_SURPLUS' ||
    value === 'MARKET_SITUATION_STABLE' ||
    value === 'MARKET_SITUATION_SHORTAGE'
  ) {
    return value;
  }

  return null;
}

function toSourcingPriceDynamicsCategory(
  value: AuctionVehicleResponseBody['priceDynamicsCategory']
) {
  if (
    value === 'PRICE_DYNAMICS_FALLING' ||
    value === 'PRICE_DYNAMICS_RISING' ||
    value === 'PRICE_DYNAMICS_STABLE'
  ) {
    return value;
  }

  return null;
}
